import {
  useLocationTemplateLazyQuery,
  useLocationTemplateOptionsQuery,
} from '@kinetic-ui/data-layer';
import { ContentPaste } from '@mui/icons-material';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { Select } from '@qfa/forms';
import { LoadingButton, useBoolean } from '@qfa/react-components';
import { useCustomSnackbar } from 'components/NotificationSnackbars/useCustomSnackbar';
import { getNodes } from 'kineticUtils/data/getNodes';
import { nanoid } from 'nanoid';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SetState } from 'types/props';
import { LocationFormValues } from '../types';
import { LocationInputToForm } from '../utils/inputToForm';
import Dialog from 'components/Dialog';
import { RecursivePartial } from 'types';

export const PopulateFromTemplateButton = ({
  setFormValues,
}: {
  setFormValues: SetState<Partial<RecursivePartial<LocationFormValues>>>;
}) => {
  const { t } = useTranslation(['locations', 'common']);

  const { data } = useLocationTemplateOptionsQuery();

  const [{ off, on }, open] = useBoolean();

  const options = useMemo(() => {
    const templates = getNodes(data?.locationTemplates);
    return templates.map((template) => ({ label: template.name, value: template.id }));
  }, [data]);

  const [loadTemplate, { loading }] = useLocationTemplateLazyQuery();
  const { enqueueCustomSnackbar } = useCustomSnackbar();

  const [templateId, setTemplateId] = useState<string | null>(null);

  const applyTemplate = () => {
    if (!templateId) {
      return;
    }
    loadTemplate({ variables: { id: templateId } })
      .then(({ data }) => {
        const templateLocationData = data?.locationTemplate.template;
        if (!templateLocationData) {
          return;
        }
        const newFormValues = LocationInputToForm(templateLocationData);
        // replace entity IDs with new ones
        const laneIdMap =
          newFormValues.lanes?.reduce((acc, lane) => {
            acc[lane.id] = nanoid();
            return acc;
          }, {} as Record<string, string>) ?? {};
        newFormValues.lanes?.forEach((lane) => {
          lane.id = laneIdMap[lane.id];
        });
        // ensure maneuvers reference the new lane IDs
        newFormValues.maneuvers?.forEach((maneuver) => {
          maneuver.id = nanoid();
          maneuver.refApproachLane = laneIdMap[maneuver.refApproachLane];
          maneuver.refDepartureLane = laneIdMap[maneuver.refDepartureLane];
        });
        newFormValues.crosswalks?.forEach((crosswalk) => {
          crosswalk.id = nanoid();
        });
        setFormValues(newFormValues);
        enqueueCustomSnackbar.success(t('applyTemplateSuccess'));
        off();
      })
      .catch(() => {
        enqueueCustomSnackbar.error(t('applyTemplateFail'));
      });
  };

  return (
    <>
      <Tooltip title={t('populateFromTemplate')}>
        <IconButton onClick={on}>
          <ContentPaste fontSize='small' />
        </IconButton>
      </Tooltip>
      <Dialog
        open={open}
        title={t('populateFromTemplate')}
        children={
          <>
            <Alert color='warning'>
              {t('applyTemplateWarning1')}
              <p>{t('applyTemplateWarning2')}</p>
              <p>
                <strong>{t('applyTemplateWarning3')}</strong>
              </p>
            </Alert>

            <Select
              id='templateId'
              name='templateId'
              label={t('common:template')}
              onChange={(_, newValue) => setTemplateId(newValue)}
              type='select'
              options={options}
            />
          </>
        }
        buttons={
          <>
            <Button variant='text' onClick={off}>
              {t('common:cancel')}
            </Button>
            <LoadingButton
              loading={loading}
              variant='contained'
              color='primary'
              disabled={!templateId}
              onClick={applyTemplate}
            >
              {t('applyTemplate')}
            </LoadingButton>
          </>
        }
      />
    </>
  );
};
